import React, { useEffect, useLayoutEffect, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { AppView, Button, ButtonGroup, rem } from 'design-system';
import StatusBar from 'design-system/StatusBar';
import Text from 'design-system/Text';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import useDisableAndroidBackButton from 'hooks/useDisableAndroidBackButton';
import { useMarginBottom } from 'hooks/useMarginBottom';
import Squircle from 'design-system/Squircle';
import useColors from 'hooks/useColors';
import { IconSearch } from 'design-system/icons';
import { IconError } from 'design-system/icons/IconError';
const formatRetryTime = (seconds) => {
    if (seconds < 60) {
        return `${seconds} ${seconds === 1 ? 'second' : 'seconds'}`;
    }
    if (seconds < 3600) {
        const minutes = Math.floor(seconds / 60);
        return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`;
    }
    if (seconds < 86400) {
        const hours = Math.floor(seconds / 3600);
        return `${hours} ${hours === 1 ? 'hour' : 'hours'}`;
    }
    const days = Math.floor(seconds / 86400);
    return `${days} ${days === 1 ? 'day' : 'days'}`;
};
const EXTRA_PADDING = rem(24);
const CreditScoreErrorScreen = ({ navigation, route, }) => {
    const { params } = route;
    const { errorTitle, errorMessage, alertIcon, retryAfterSeconds, primaryButtonText, primaryButtonOnPress, secondaryButtonText, secondaryButtonOnPress, tertiaryButtonText, tertiaryButtonOnPress, } = params;
    const { paddingHorizontalStyle } = useAppFrameDimensions();
    const { top } = useSafeAreaInsets();
    const paddingBottomStyle = useMarginBottom('paddingBottom');
    const colors = useColors();
    useLayoutEffect(() => {
        navigation.setOptions({
            headerBackVisible: false,
            gestureEnabled: false,
        });
    }, [navigation]);
    useDisableAndroidBackButton();
    const [count, setCount] = useState(Math.max(retryAfterSeconds ?? 0, 0));
    useEffect(() => {
        setCount(Math.max(retryAfterSeconds ?? 0, 0));
        if (!retryAfterSeconds)
            return undefined;
        const interval = setInterval(() => {
            setCount((prev) => {
                if (prev <= 1) {
                    clearInterval(interval);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
        return () => clearInterval(interval);
    }, [retryAfterSeconds]);
    return (<AppView withFrame={false}>
      <StatusBar barStyle="dark-content" animated/>
      <View style={[
            styles.content,
            paddingHorizontalStyle,
            { paddingTop: top + EXTRA_PADDING },
        ]}>
        <Squircle width={rem(88)} height={rem(88)} color={alertIcon
            ? colors.elements.statusInformationalLight
            : colors.elements.statusOfflineLight}>
          {alertIcon ? (<IconError gradientColors={[
                colors.elements.statusInformational,
                colors.elements.statusInformational,
            ]} width={54} height={54}/>) : (<IconSearch alt gradientColors={[
                colors.elements.statusOffline,
                colors.elements.statusOffline,
            ]} width={54} height={54}/>)}
        </Squircle>
        <View style={styles.textContainer}>
          <Text Text-20 centered>
            {errorTitle}
          </Text>
          {!!errorMessage && (<View style={styles.paragraphs}>
              {(Array.isArray(errorMessage)
                ? errorMessage
                : [errorMessage]).map((paragraph) => (<Text TextThin-14 Secondary centered key={paragraph}>
                  {paragraph}
                </Text>))}
            </View>)}
          {!!retryAfterSeconds && (<Text TextThin-14 Secondary centered>
              {count
                ? `You can try again in ${formatRetryTime(count)}.`
                : 'Please try again.'}
            </Text>)}
        </View>
      </View>
      <ButtonGroup style={[paddingHorizontalStyle, paddingBottomStyle]}>
        {!!(tertiaryButtonText && tertiaryButtonOnPress) && (<Button brandBorder title={tertiaryButtonText} onPress={tertiaryButtonOnPress}/>)}
        {!!(secondaryButtonText && secondaryButtonOnPress) && (<Button brandReversed title={secondaryButtonText} onPress={secondaryButtonOnPress}/>)}
        {!!(primaryButtonText && primaryButtonOnPress) && (<Button brand title={primaryButtonText} onPress={primaryButtonOnPress}/>)}
      </ButtonGroup>
    </AppView>);
};
const styles = StyleSheet.create({
    content: {
        flex: 1,
        gap: rem(24),
        alignItems: 'center',
        justifyContent: 'center',
        marginHorizontal: rem(16),
    },
    textContainer: {
        gap: rem(8),
    },
    paragraphs: {
        gap: rem(10),
    },
});
export default CreditScoreErrorScreen;
